import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addEquipment, clearError } from '../redux/slices/equipmentsSlice';
import { toast } from 'react-toastify';
import { LoadingModal } from '../components/LoadingModal';
import { LocationAutocomplete } from './LocationAutocomplete';
import { BulkAddEquipmentModal } from './BulkAddEquipmentModal';

const AddEquipmentModal = ({ setAddEquipmentModal }) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.equipments.loading);
  const error = useSelector((state) => state.equipments.error);

  const [view, setView] = useState('selection'); // 'selection', 'form', or 'bulkAdd'
  const [name, setName] = useState('');
  const [qty, setQty] = useState('');
  const [rentalPrice, setRentalPrice] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [images, setImages] = useState([]);
  const [imageInput, setImageInput] = useState('');
  const [specs, setSpecs] = useState('');
  const [description, setDescription] = useState('');
  const [tutorials, setTutorials] = useState('');
  const [selfTransport, setSelfTransport] = useState('');
  const [translations, setTranslations] = useState({
    en: { name: '', category: '', description: '' },
    es: { name: '', category: '', description: '' },
  });

  useEffect(() => {
    // Clear any existing errors when component mounts
    dispatch(clearError());

    // Clean up when component unmounts
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleDismissError = () => {
    dispatch(clearError());
  };

  const handleAddImage = () => {
    if (imageInput.trim()) {
      setImages((prevImages) => [...prevImages, imageInput.trim()]);
      setImageInput('');
    }
  };

  const handleRemoveImage = (index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const parsedQty = parseInt(qty, 10);
    const parsedRentalPrice = parseFloat(rentalPrice);

    if (isNaN(parsedQty) || isNaN(parsedRentalPrice)) {
      toast.error('Please enter valid numbers for quantity and rental price.');
      return;
    }

    console.log('=== SUBMITTING EQUIPMENT ===');
    console.log('name:', name);
    console.log('qty:', parsedQty);
    console.log('category:', category);
    console.log('description:', description);
    console.log('selfTransport:', selfTransport);
    console.log('translations:', translations);
    console.log('translations.en:', translations.en);

    try {
      await dispatch(
        addEquipment({
          name,
          qty: parsedQty,
          rentalPrice: parsedRentalPrice,
          location,
          category,
          images,
          specs,
          description,
          tutorials,
          selfTransport,
          translations,
        })
      ).unwrap();

      setName('');
      setQty('');
      setRentalPrice('');
      setLocation('');
      setCategory('');
      setImages([]);
      setSpecs('');
      setDescription('');
      setTutorials('');
      setSelfTransport('');
      setTranslations({
        en: { name: '', category: '', description: '' },
        es: { name: '', category: '', description: '' },
      });
      toast.success('Equipment added successfully!');
      setAddEquipmentModal(false);
    } catch (error) {
      console.error('Failed to add equipment:', error);
    }
  };

  const canSubmit =
    name &&
    qty &&
    rentalPrice &&
    location &&
    category &&
    specs &&
    description &&
    selfTransport &&
    tutorials &&
    translations.en.name &&
    translations.en.category &&
    translations.en.description;

  // Bulk add view
  if (view === 'bulkAdd') {
    return (
      <BulkAddEquipmentModal
        setBulkAddModal={(show) => {
          if (!show) {
            setView('selection');
          }
        }}
        setAddEquipmentModal={setAddEquipmentModal}
      />
    );
  }

  // Selection page view
  if (view === 'selection') {
    return (
      <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-8 rounded-lg w-80 flex flex-col items-center space-y-6">
          <h3 className="text-xl font-bold">Add Equipment</h3>
          <div className="w-full flex flex-col space-y-4">
            <button
              type="button"
              onClick={() => setView('form')}
              className="bg-[#2E7A32] hover:bg-green-500 text-white font-bold py-3 px-6 rounded-full w-full"
            >
              Input details manually
            </button>
            <button
              type="button"
              onClick={() => setView('bulkAdd')}
              className="bg-[#2E7A32] hover:bg-green-500 text-white font-bold py-3 px-6 rounded-full w-full"
            >
              Upload CSV File
            </button>
            <button
              type="button"
              onClick={() => setAddEquipmentModal(false)}
              className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-3 px-6 rounded-full w-full"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form view (existing add equipment form)
  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      {(loading || error) && (
        <LoadingModal message="Adding equipment..." error={error} onClose={handleDismissError} />
      )}
      <div className="bg-white p-6 rounded-lg w-1/3 h-3/4 overflow-hidden flex flex-col">
        <h3 className="text-lg font-bold mb-4">Add New Equipment</h3>
        <div className="overflow-y-auto flex-grow pr-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name (English) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-en-name">
                Name (English)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="translations-en-name"
                value={translations.en.name}
                onChange={(e) => {
                  setTranslations({
                    ...translations,
                    en: { ...translations.en, name: e.target.value },
                  });
                  setName(e.target.value);
                }}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Name (Spanish) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-es-name">
                Name (Spanish)
              </label>
              <input
                type="text"
                id="translations-es-name"
                value={translations.es.name}
                onChange={(e) =>
                  setTranslations({
                    ...translations,
                    es: { ...translations.es, name: e.target.value },
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Category (English) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-en-category">
                Category (English)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="translations-en-category"
                value={translations.en.category}
                onChange={(e) => {
                  setTranslations({
                    ...translations,
                    en: { ...translations.en, category: e.target.value },
                  });
                  setCategory(e.target.value);
                }}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Category (Spanish) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-es-category">
                Category (Spanish)
              </label>
              <input
                type="text"
                id="translations-es-category"
                value={translations.es.category}
                onChange={(e) =>
                  setTranslations({
                    ...translations,
                    es: { ...translations.es, category: e.target.value },
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            {/* Description (English) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-en-description">
                Description (English)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="translations-en-description"
                value={translations.en.description}
                onChange={(e) => {
                  setTranslations({
                    ...translations,
                    en: { ...translations.en, description: e.target.value },
                  });
                  setDescription(e.target.value);
                }}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
                placeholder="E.g. Heavy-duty rotary tiller ideal for breaking up compact soil and preparing seedbeds. Easy to attach to most tractors."
                required
              />
            </div>

            {/* Description (Spanish) */}
            <div>
              <label className="block font-semibold" htmlFor="translations-es-description">
                Description (Spanish)
              </label>
              <textarea
                id="translations-es-description"
                value={translations.es.description}
                onChange={(e) =>
                  setTranslations({
                    ...translations,
                    es: { ...translations.es, description: e.target.value },
                  })
                }
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
              />
            </div>

            {/* Quantity */}
            <div>
              <label className="block font-semibold" htmlFor="qty">
                Quantity
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                id="qty"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Rental Price */}
            <div>
              <label className="block font-semibold" htmlFor="rentalPrice">
                Rental Price
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                id="rentalPrice"
                value={rentalPrice}
                onChange={(e) => setRentalPrice(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Location */}
            <div>
              <LocationAutocomplete
                id="location"
                name="location"
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                required={true}
              />
            </div>

            {/* Images */}
            <div>
              <label className="block font-semibold" htmlFor="images">
                Image URLs
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  id="imageInput"
                  value={imageInput}
                  onChange={(e) => setImageInput(e.target.value)}
                  className="flex-grow p-2 border border-gray-300 rounded-md"
                  placeholder="Enter image URL"
                />
                <button
                  type="button"
                  onClick={handleAddImage}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold px-4 rounded-md"
                >
                  Add
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {images.map((image, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <img
                      src={image}
                      alt={`Preview ${index}`}
                      className="w-16 h-16 object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="text-red-500 text-xs mt-1"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-semibold" htmlFor="tutorials">
                Self-Transport Requirements
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="selfTransport"
                value={selfTransport}
                onChange={(e) => setSelfTransport(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="4"
                placeholder="If user selects they do not need pickup or delivery assistance. E.g. Requires heavy-duty trailer, 2-ton towing capacity, flatbed with ramps, tie-down straps, etc."
                required
              />
            </div>

            {/* Specifications */}
            <div>
              <label className="block font-semibold" htmlFor="specs">
                Specifications
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="specs"
                value={specs}
                onChange={(e) => setSpecs(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="2"
                placeholder="E.g. 6 ft working width, 540 RPM PTO, requires 30+ HP tractor, adjustable tilling depth up to 6 inches."
                required
              />
            </div>

            {/* Tutorials */}
            <div>
              <label className="block font-semibold" htmlFor="tutorials">
                Tutorials
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="tutorials"
                value={tutorials}
                onChange={(e) => setTutorials(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
                placeholder="E.g. Connect PTO before lowering tiller. Keep bystanders at least 10 ft away. Watch video on attaching tiller safely. [Link to video]"
                required
              />
            </div>
          </form>
        </div>
        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={() => setView('selection')}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-md"
          >
            Back
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !canSubmit}
          >
            {loading ? 'Adding...' : 'Add Equipment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export { AddEquipmentModal };
